import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useYacht } from "../../context/YachtContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import ProfileForm from "../../components/crew/ProfileForm";
import Settings from "./Settings";
import SupplierCompanyDetails from "./profile/SupplierCompanyDetails";
import { titleCase, STATUS_STYLE, statusLabel } from "../../lib/crewHr";
import { Loader2, AlertTriangle, Ship, MapPin, Flag } from "lucide-react";

const fmtErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const API = process.env.REACT_APP_BACKEND_URL;

const PERM_GROUPS = [
  { label: "Inventory", actions: [
    ["inventory:create", "Add item"],
    ["inventory:edit", "Edit item"],
    ["inventory:delete", "Delete item"],
    ["inventory:movement", "Record stock movement"],
    ["inventory:consumption", "View consumption chart"],
  ]},
  { label: "Maintenance", actions: [
    ["maintenance:create", "Create job"],
    ["maintenance:edit", "Edit / update status"],
    ["maintenance:delete", "Delete job"],
  ]},
  { label: "Tasks", actions: [
    ["task:create", "Create task"],
    ["task:edit", "Edit task"],
    ["task:delete", "Delete task"],
  ]},
  { label: "Logbook", actions: [
    ["logbook:create", "Add entry"],
    ["logbook:delete", "Delete entry"],
  ]},
  { label: "Petty cash", actions: [
    ["pettycash:create", "Submit request"],
    ["pettycash:approve", "Approve / reject"],
    ["pettycash:delete", "Delete entry"],
  ]},
  { label: "Documents", actions: [
    ["document:create", "Upload"],
    ["document:delete", "Delete"],
  ]},
  { label: "Photos", actions: [
    ["photo:create", "Upload"],
    ["photo:delete", "Delete"],
  ]},
  { label: "Yacht & crew", actions: [
    ["yacht:edit", "Edit yacht specs"],
    ["crew:invite", "Invite crew"],
    ["crew:remove", "Remove crew"],
    ["report:export", "Export reports"],
  ]},
];

function YachtPermissions({ yacht }) {
  const rights = yacht.my_rights || [];
  const groups = PERM_GROUPS
    .map((g) => ({ ...g, allowed: g.actions.filter(([a]) => rights.includes(a)) }))
    .filter((g) => g.allowed.length > 0);
  return (
    <div className="mt-3 border-t border-zinc-100 pt-3" data-testid={`yacht-permissions-${yacht.id}`}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-2">
        Permissions — what you can do here
      </div>
      {groups.length === 0 ? (
        <div className="text-xs text-zinc-500 italic">
          Your role has no general yacht-wide permissions. You'll see items specifically assigned to you on the dashboard.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-700 mb-1">{g.label}</div>
              <ul className="space-y-1">
                {g.allowed.map(([action, label]) => (
                  <li key={action} className="flex items-center gap-2 text-sm text-zinc-700" data-testid={`perm-${yacht.id}-${action}`}>
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />{label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CrewProfile() {
  const { user, setUser } = useAuth();
  const { yachts } = useYacht();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crew/profile/me");
      setForm(data);
      setAlerts(data.cert_alerts || []);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setLoading(false); }
  };
  // Loads on mount, and again whenever the account-managed identity fields
  // (name/phone/email, edited in the Account preferences tab) change — so the
  // read-only mirror in "My personal details" stays in sync without a reload.
  useEffect(() => { load(); }, [user?.name, user?.phone, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/crew/profile/me", form);
      setForm(data);
      setAlerts(data.cert_alerts || []);
      if (data.full_name) setUser((u) => ({ ...u, name: data.full_name }));
      toast.success("Profile saved");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };

  if (loading || !form) {
    return <div className="flex items-center gap-2 text-zinc-500 p-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading your profile…</div>;
  }

  const myYachts = yachts || [];

  return (
    <div className="p-6 lg:p-10 max-w-4xl" data-testid="crew-profile-page">
      <div className="mb-2 text-xs uppercase tracking-wide text-zinc-400">My account</div>
      <h1 className="font-display text-3xl mb-5">My profile</h1>

      <Tabs defaultValue="account">
        <TabsList className="flex w-full max-w-full justify-start flex-wrap h-auto gap-y-1 [&>button]:shrink-0" data-testid="profile-tabs">
          <TabsTrigger value="account" data-testid="tab-account">Account preferences</TabsTrigger>
          {(user?.is_supplier || user?.account_kind === "provider") && <TabsTrigger value="company" data-testid="tab-company">Company details</TabsTrigger>}
          <TabsTrigger value="assignments" data-testid="tab-assignments">Yacht assignments</TabsTrigger>
          {!(user?.is_supplier || user?.account_kind === "provider") && <TabsTrigger value="personal" data-testid="tab-personal">My personal details</TabsTrigger>}
        </TabsList>

        {/* 1) Account preferences */}
        <TabsContent value="account" className="mt-5" data-testid="account-settings-section">
          <p className="text-sm text-zinc-500 mb-3">Manage your account name, phone, email, password and language here. Phone and email changes require a verification code.</p>
          <Settings embedded />
        </TabsContent>

        {/* 2) Yacht assignments */}
        <TabsContent value="assignments" className="mt-5" data-testid="assignments-section">
          {myYachts.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500">
              You're not assigned to any yacht yet.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-zinc-500">You're assigned to {myYachts.length} yacht{myYachts.length > 1 ? "s" : ""}.</p>
              {myYachts.map((y) => (
                <div key={y.id} className="rounded-xl border border-zinc-200 bg-white overflow-hidden flex flex-col sm:flex-row" data-testid={`assignment-${y.id}`}>
                  <div className="sm:w-44 h-32 sm:h-auto bg-zinc-100 shrink-0 flex items-center justify-center text-zinc-300">
                    {y.cover_image
                      ? <img alt="" className="h-full w-full object-cover" src={`${API}/api/files/${y.cover_image}`} />
                      : <Ship className="h-8 w-8" />}
                  </div>
                  <div className="p-4 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-display text-lg">{y.name}</h3>
                      <Badge className="bg-ink text-white">{titleCase(y.my_role)}</Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-zinc-600">
                      {(y.brand || y.model) && <div><span className="text-zinc-400">Make: </span>{[y.brand, y.model].filter(Boolean).join(" ")}</div>}
                      {y.length_m && <div><span className="text-zinc-400">Length: </span>{y.length_m} m</div>}
                      {y.year && <div><span className="text-zinc-400">Year: </span>{y.year}</div>}
                      {y.flag && <div className="flex items-center gap-1"><Flag className="h-3 w-3 text-zinc-400" />{y.flag}</div>}
                      {y.registration_no && <div><span className="text-zinc-400">Reg: </span>{y.registration_no}</div>}
                      {y.home_marina && <div className="flex items-center gap-1"><MapPin className="h-3 w-3 text-zinc-400" />{y.home_marina}</div>}
                    </div>
                    <YachtPermissions yacht={y} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* 3) My personal details */}
        {!(user?.is_supplier || user?.account_kind === "provider") && (
        <TabsContent value="personal" className="mt-5" data-testid="personal-section">
          <p className="text-sm text-zinc-500 mb-4">
            This profile belongs to you and travels with you. A manager can pull a copy into a yacht — but their edits never change your profile.
          </p>

          {alerts.length > 0 && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="profile-cert-alerts">
              <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-2">
                <AlertTriangle className="h-4 w-4" /> {alerts.length} certificate{alerts.length > 1 ? "s" : ""} need attention
              </div>
              <div className="flex flex-wrap gap-2">
                {alerts.map((a, i) => (
                  <Badge key={i} className={`${STATUS_STYLE[a.status]} border`}>{a.cert_label}: {statusLabel(a.status, a.days_left)}</Badge>
                ))}
              </div>
            </div>
          )}

          <ProfileForm value={form} set={setForm} yachtId={null} lockIdentity />

          <div className="mt-6 flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-ink hover:bg-black" data-testid="save-profile">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save profile
            </Button>
          </div>
        </TabsContent>
        )}

        {/* 4) Company details (marketplace suppliers only) */}
        {(user?.is_supplier || user?.account_kind === "provider") && (
          <TabsContent value="company" className="mt-5" data-testid="company-section">
            <SupplierCompanyDetails />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
